import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
} from 'recharts';
import type { TrendData } from '@/types/transaction';

export interface LineConfig {
  key: string;
  color: string;
  name?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface TrendLineChartProps {
  data: TrendData[] | any[];
  height?: number;
  showArea?: boolean;
  showLegend?: boolean;
  xKey?: string;
  lines?: LineConfig[];
}

const formatCurrency = (value: number) => {
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(1)}k`;
  }
  return `R$ ${value.toFixed(0)}`;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-lg p-3 border border-gray-200 dark:border-secondary-700">
        <p className="font-medium text-gray-900 dark:text-white mb-2">
          {label ? formatDate(label) : ''}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600 dark:text-gray-400">
              {entry.name === 'income'
                ? 'Receita'
                : entry.name === 'expense'
                ? 'Despesa'
                : 'Saldo'}
              :
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

interface CustomLegendProps {
  payload?: Array<{
    value: string;
    color: string;
  }>;
}

const CustomLegend: React.FC<CustomLegendProps> = ({ payload }) => {
  if (!payload) return null;

  const labels: Record<string, string> = {
    income: 'Receita',
    expense: 'Despesa',
    balance: 'Saldo',
  };

  return (
    <div className="flex justify-center gap-6 mt-4">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {labels[entry.value] || entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export const TrendLineChart: React.FC<TrendLineChartProps> = ({
  data,
  height = 300,
  showArea = false,
  showLegend = true,
  xKey,
  lines,
}) => {
  // Transform data to add formatted date (only if using default date field)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartData = data.map((item: any) => ({
    ...item,
    formattedDate: xKey ? item[xKey] : formatDate(item.date),
  }));

  if (data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-gray-500 dark:text-gray-400"
        style={{ height }}
      >
        Sem dados para exibir
      </div>
    );
  }

  const ChartComponent = showArea ? AreaChart : LineChart;

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            className="dark:stroke-secondary-700"
          />
          <XAxis
            dataKey="formattedDate"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickFormatter={formatCurrency}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            width={70}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && <Legend content={<CustomLegend />} />}

          {lines ? (
            // Custom lines configuration
            lines.map((line) => (
              <Line
                key={line.key}
                type="monotone"
                dataKey={line.key}
                name={line.name || line.key}
                stroke={line.color}
                strokeWidth={2}
                dot={{ fill: line.color, strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: line.color }}
              />
            ))
          ) : showArea ? (
            <>
              <defs>
                <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="income"
                stroke="#22c55e"
                fill="url(#incomeGradient)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: '#22c55e' }}
              />
              <Area
                type="monotone"
                dataKey="expense"
                stroke="#ef4444"
                fill="url(#expenseGradient)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: '#ef4444' }}
              />
            </>
          ) : (
            <>
              <Line
                type="monotone"
                dataKey="income"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ fill: '#22c55e', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#22c55e' }}
              />
              <Line
                type="monotone"
                dataKey="expense"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ fill: '#ef4444', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#ef4444' }}
              />
              <Line
                type="monotone"
                dataKey="balance"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#3b82f6', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#3b82f6' }}
              />
            </>
          )}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

// Simple version without Recharts
export const TrendLineChartSimple: React.FC<TrendLineChartProps> = ({ data }) => {
  // Type guard to ensure we have TrendData
  const trendData = data as TrendData[];

  if (trendData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        Sem dados para exibir
      </div>
    );
  }

  const maxValue = Math.max(...trendData.flatMap((d) => [d.income, d.expense, d.balance]));

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Receitas</p>
          <p className="text-lg font-bold text-green-600 dark:text-green-400">
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(trendData.reduce((sum, d) => sum + d.income, 0))}
          </p>
        </div>
        <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Despesas</p>
          <p className="text-lg font-bold text-red-600 dark:text-red-400">
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(trendData.reduce((sum, d) => sum + d.expense, 0))}
          </p>
        </div>
        <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <p className="text-sm text-gray-500 dark:text-gray-400">Saldo Final</p>
          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
            {new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(trendData[trendData.length - 1]?.balance || 0)}
          </p>
        </div>
      </div>

      {/* Simple bar representation */}
      <div className="space-y-2">
        {trendData.map((item, index) => (
          <div key={index} className="space-y-1">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{formatDate(item.date)}</span>
              <span>Saldo: {formatCurrency(item.balance)}</span>
            </div>
            <div className="flex gap-1 h-4">
              <div
                className="bg-green-500 rounded"
                style={{ width: `${(item.income / maxValue) * 100}%` }}
                title={`Receita: ${formatCurrency(item.income)}`}
              />
              <div
                className="bg-red-500 rounded"
                style={{ width: `${(item.expense / maxValue) * 100}%` }}
                title={`Despesa: ${formatCurrency(item.expense)}`}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span className="text-gray-600 dark:text-gray-400">Receita</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span className="text-gray-600 dark:text-gray-400">Despesa</span>
        </div>
      </div>
    </div>
  );
};

export default TrendLineChart;
